import { useRef } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { FILE_UPLOAD_STATUS, FOOTER_TYPES } from "../../constants";
import { Check } from "../../assets/svgs";
import { TarabutFaviconLoader } from "../../assets/gif";
import useApp from "../../hooks/useApp";
import PoweredByLogo from "../PoweredBy";
import "./styles.scss";

const CustomButton = ({
  disabled,
  isLoading,
  btnVariant = "btn-primary-600",
  text,
  onClick,
  style,
  type,
  url,
  buttonText,
  rightIcon,
  leftIcon,
  action,
  buttonClassText,
  containerClassName,
  isCheckBoxVisible,
  containerCheckbox,
  checkboxValue,
  onChangeCheckBox,
  checkboxLabel,
  isPoweredByLogoVisible,
  providerName = "",
  isLogoLoader,
  isFileUpload = false,
  onClickFileUpload,
  showButton = true,
  buttonClassName,
  removeDisplayFlex,
  expiresIn,
}) => {
  const {
    isRTL,
    handleSetFileUploadProgress,
    handleSetFileUploadDetails,
    handleSetFileUploadStatus,
  } = useApp();
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const { pathname } = location;
  const routeName = pathname.split("/")[1];
  const intentId = searchParams.get("intentId");
  const status = searchParams.get("status");

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB in bytes

    if (!file) return;

    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      return;
    }

    handleSetFileUploadDetails({
      name: file.name,
      size: `${(file.size / 1024).toFixed(0)} ${t(
        `${routeName}.${"file_size_kb"}`
      )}`,
    });
    handleSetFileUploadProgress(1);
    handleSetFileUploadStatus(FILE_UPLOAD_STATUS.UPLOADING);

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result;
      handleSetFileUploadDetails({
        name: file.name,
        size: `${(file.size / 1024).toFixed(0)} ${t(
          `${routeName}.${"file_size_kb"}`
        )}`,
        file,
        base64: base64String,
        type: file.type,
      });
      onClickFileUpload({
        file,
        base64: base64String,
        type: file.type,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleButtonText = () => {
    if (expiresIn > 0) {
      return `${routeName}.try_again_button_text`;
    } else {
      return `${routeName}.${buttonText}`;
    }
  };

  const handlePointerDown = () => {
    if (isFileUpload) {
      fileInputRef.current?.click();
      return;
    }

    const shouldSkipAction =
      url && JSON.parse(url)?.value && !intentId && !status;
    if (!shouldSkipAction) {
      onClick(action);
    }
  };

  if (!showButton) return null;

  return (
    <div
      className={`button-wrapper ${containerClassName}`}
      style={removeDisplayFlex && { flex: 0 }}
    >
      {isPoweredByLogoVisible && (
        <PoweredByLogo poweredByText={t(`${routeName}.powered_by`)} />
      )}
      {isCheckBoxVisible && (
        <div className={`check-box-container ${containerCheckbox}`}>
          <span
            className={`check-box ${!!checkboxValue && "check-box-checked"} ${
              isRTL && "check-box-rtl"
            }`}
            onClick={onChangeCheckBox}
          >
            {checkboxValue ? (
              <img src={Check} alt="check" className="check-icon" />
            ) : null}
          </span>
          <span className="check-box-label">{checkboxLabel}</span>
        </div>
      )}
      {isFileUpload ? (
        <input
          ref={fileInputRef}
          type="file"
          id="upload-input"
          accept="image/*,application/pdf"
          hidden
          onChange={handleFileUpload}
        />
      ) : null}
      <button
        disabled={disabled || isLoading}
        className={classNames(
          `btn ${btnVariant} button-container ${buttonClassName}`,
          {
            "default-button": type === FOOTER_TYPES.DEFAULT_BUTTON,
          }
        )}
        style={{
          width: `${style?.maxWidth}%`,
          alignSelf: "center",
          ...style,
        }}
        // onClick={() => (url && !intentId && !status ? null : onClick(action))}
        // onTouchStart={() => (url && !intentId && !status ? null : onClick(action))}
        onPointerDown={handlePointerDown}
      >
        {isLoading && !isLogoLoader ? (
          <Spinner
            animation="border"
            role="status"
            className="spinner-border-sm"
          />
        ) : (
          <>
            {leftIcon ? (
              <img
                src={leftIcon}
                alt="left icon"
                className="button-left-icon"
              />
            ) : null}
            <span className={`button-text ${buttonClassText}`}>
              {text ??
                t(routeName ? handleButtonText() : `${buttonText}`, {
                  providerName: providerName,
                  expiresIn: expiresIn,
                })}
            </span>
            {rightIcon ? (
              <img
                src={rightIcon}
                alt="right icon"
                className="button-right-icon"
              />
            ) : null}
            {isLogoLoader ? (
              <img
                src={TarabutFaviconLoader}
                alt="logo loader"
                className="logo-loader"
              />
            ) : null}
          </>
        )}
      </button>
    </div>
  );
};

export default CustomButton;
